import { useTranslation } from '../i18n'
import { COLORS } from '../lib/theme'
import { uiLocale, weekdaysShortMonFirst } from '../lib/dateFormat'

export interface ActivityWeekCardProps {
  bars: number[]
  gameReported: number | string
  hoursInField: number | string
  mapPins: number | string
  /**
   * Legacy team-scoped metric. "N people were on hunts with me" answers no
   * question the viewer can act on — prefer `hunts`, which is the denominator
   * the other three numbers need. Rendered only when `hunts` is absent.
   */
  participants?: number | string
  /**
   * Hunts in the selected window. When provided it takes the fourth metric
   * slot (replacing `participants`), making the card fully personal: hours,
   * game and pins only make sense against how many hunts they came from.
   */
  hunts?: number | string

  /** Header text. Defaults to "Activity". */
  title?: string
  /** Sub-header text under the title. Defaults to "Last 7 days". */
  subtitle?: string
  /** One label per bar (must be length 7 if provided). Defaults to Mon..Sun. */
  labels?: string[]
  /**
   * Index of the bar to highlight (e.g., today). Range 0..6, or -1 / undefined for none.
   * When `labels` is omitted, defaults to today's weekday for backwards compat.
   */
  highlightIndex?: number

  /** If provided, render a chip-group selector for range presets (e.g., [7, 14, 30, 60, 90]). */
  rangeOptions?: number[]
  /** Currently selected range (must be in rangeOptions). */
  selectedRange?: number
  /** Called when the user picks a different range chip. */
  onRangeChange?: (days: number) => void
}

function computeTodayWeekdayIndex(): number {
  const day = new Date().getDay()
  return day === 0 ? 6 : day - 1
}

export function ActivityWeekCard({
  bars,
  gameReported,
  hoursInField,
  mapPins,
  participants,
  hunts,
  title,
  subtitle,
  labels,
  highlightIndex,
  rangeOptions,
  selectedRange,
  onRangeChange,
}: ActivityWeekCardProps) {
  const { t, i18n } = useTranslation('overview')
  if (bars.length !== 7) {
    throw new Error('ActivityWeekCard: `bars` must contain exactly 7 values')
  }
  const resolvedTitle = title ?? t('activity.title')
  const resolvedSubtitle = subtitle ?? t('activity.lastDays', { count: selectedRange ?? bars.length })
  const resolvedLabels = labels ?? weekdaysShortMonFirst(uiLocale(i18n.language))
  if (resolvedLabels.length !== 7) {
    throw new Error('ActivityWeekCard: `labels` must contain exactly 7 entries when provided')
  }
  const resolvedHighlight =
    highlightIndex !== undefined ? highlightIndex : labels ? -1 : computeTodayWeekdayIndex()
  const maxBar = Math.max(...bars, 1)
  const showChips = Array.isArray(rangeOptions) && rangeOptions.length > 0

  return (
    <div className="wh-card" style={{ padding: 20 }}>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.onSurface }}>{resolvedTitle}</div>
          <div style={{ fontSize: 12, color: COLORS.outline, marginTop: 2 }}>{resolvedSubtitle}</div>
        </div>
        {showChips && (
          /* Segmented tabs — same style as the Hunts screen's status filter
             (flat track, white active segment with a soft shadow), sized down
             a notch to fit the card's header row. */
          <div
            role="group"
            aria-label={t('activity.timeRange')}
            style={{ display: 'flex', gap: 2, background: COLORS.surface, padding: 3, borderRadius: 8, flexShrink: 0 }}
          >
            {rangeOptions!.map((opt) => {
              const isActive = opt === selectedRange
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => onRangeChange?.(opt)}
                  aria-pressed={isActive}
                  style={{
                    border: 'none',
                    padding: '5px 10px',
                    borderRadius: 6,
                    background: isActive ? COLORS.surfaceContainerLowest : 'transparent',
                    boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                    fontSize: 12,
                    fontWeight: 600,
                    color: isActive ? COLORS.primary : COLORS.outline,
                    cursor: 'pointer',
                    lineHeight: 1.2,
                  }}
                >
                  {t('activity.rangeDays', { count: opt })}
                </button>
              )
            })}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 64, marginBottom: 8 }}>
        {resolvedLabels.map((labelText, i) => (
          <div
            key={`${labelText}-${i}`}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}
          >
            <div
              style={{
                width: '100%',
                height: `${(bars[i] / maxBar) * 52}px`,
                borderRadius: 4,
                background: i === resolvedHighlight ? COLORS.primary : COLORS.secondaryFixedDim,
              }}
            />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {resolvedLabels.map((labelText, i) => (
          <div
            key={`${labelText}-${i}`}
            style={{ flex: 1, textAlign: 'center', fontSize: 11, color: COLORS.outline }}
          >
            {labelText}
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {[
          // Hunts leads when provided: it is the denominator that gives the
          // window's hours/game/pins their meaning.
          ...(hunts != null ? [{ label: t('activity.hunts'), value: hunts }] : []),
          { label: t('activity.gameReported'), value: gameReported },
          { label: t('activity.hoursInField'), value: hoursInField },
          { label: t('activity.mapPins'), value: mapPins },
          ...(hunts == null && participants != null
            ? [{ label: t('activity.participants'), value: participants }]
            : []),
        ].map(({ label, value }) => (
          <div key={label}>
            <div style={{ fontSize: 11, color: COLORS.outline, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase' }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: COLORS.onSurface, marginTop: 2 }}>{value}</div>
          </div>
        ))}
      </div>
    </div>
  )
}
